import React from "react";
import { ESTADOS_ACADEMICOS } from "../constants/estadosAcademicos";
import useCentrosListado from "../hooks/useCentrosListado";
import { showToast } from "../../../../utils/showToast";
import { soloDigitosCedula, esCedulaCompleta, mensajeErrorCedula } from "../../../../utils/documentoIdentidad";

const ESTADOS_ACADEMICOS_EDITABLES = new Set(["preinscrito", "inscrito", "rechazado"]);
const ESTADOS_ACADEMICOS_AUTOMATICOS = new Set(["matriculado", "egresado", "acreditado"]);

// OJO: nunca usar new Date().toISOString().slice(0,10) para "hoy" — toISOString()
// convierte a UTC, y en Ecuador (UTC-5) desde las 19:00 ya marca el dia siguiente.
const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function Modal({ open, title, onClose, size = "modal-lg", children }) {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
      aria-modal="true"
      role="dialog"
    >
      <div className={`modal-dialog ${size} modal-dialog-centered`} role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function EstudianteFormModal({
  modal,
  formNuevo,
  setFormNuevo,
  formEditar,
  setFormEditar,
  saving,
  deleting,
  cerrarModal,
  handleCrear,
  handleActualizar,
  handleEliminar,
  estudianteEditando,
}) {
  const { centros, loadingCentros } = useCentrosListado();
  const renderForm = (form, setForm, onSubmit, isEdit = false) => {
    const estadoActual = String(form.estado_academico || "").trim().toLowerCase();
    const estadoAutomatico = ESTADOS_ACADEMICOS_AUTOMATICOS.has(estadoActual);
    const estadosEditables = ESTADOS_ACADEMICOS.filter((estado) => ESTADOS_ACADEMICOS_EDITABLES.has(estado.value));
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!esCedulaCompleta(form.documento_identidad)) {
            showToast({ type: "warning", message: mensajeErrorCedula(form.documento_identidad) });
            return;
          }
          onSubmit?.();
        }}
      >
        <div className="modal-body pt-0">
          <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label management-label">Nombres</label>
            <input
              className="form-control"
              placeholder="Ej: Juan Carlos"
              value={form.nombres}
              onChange={(e) => setForm((prev) => ({ ...prev, nombres: e.target.value.toUpperCase() }))}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label management-label">Apellidos</label>
            <input
              className="form-control"
              placeholder="Ej: Perez Gomez"
              value={form.apellidos}
              onChange={(e) => setForm((prev) => ({ ...prev, apellidos: e.target.value.toUpperCase() }))}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label management-label">Cédula</label>
            <input
              className="form-control"
              placeholder="Ej: 1712345678"
              inputMode="numeric"
              maxLength={10}
              value={form.documento_identidad}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, documento_identidad: soloDigitosCedula(e.target.value) }))
              }
            />
          </div>
          {!isEdit && (
            <div className="col-md-6">
              <label className="form-label management-label">
                Fecha de nacimiento <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={form.fecha_nacimiento}
                onChange={(e) => setForm((prev) => ({ ...prev, fecha_nacimiento: e.target.value }))}
                max={hoy()}
                required
              />
            </div>
          )}
          <div className="col-md-6">
            <label className="form-label management-label">Celular</label>
            <input
              className="form-control"
              placeholder="Ej: 0991234567"
              value={form.celular}
              onChange={(e) => setForm((prev) => ({ ...prev, celular: e.target.value }))}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label management-label">Email</label>
            <input
              type="email"
              className="form-control"
              placeholder="email@ademy.com"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label management-label">Estado academico</label>
            <select
              className="form-select"
              value={form.estado_academico}
              onChange={(e) => setForm((prev) => ({ ...prev, estado_academico: e.target.value }))}
              disabled={estadoAutomatico}
            >
              {estadoAutomatico ? (
                <option value={form.estado_academico}>{`${form.estado_academico} (automatico)`}</option>
              ) : null}
              {estadosEditables.map((estado) => (
                <option key={estado.value} value={estado.value}>
                  {estado.label}
                </option>
              ))}
            </select>
            <small className="text-muted d-block mt-1">
              {estadoAutomatico
                ? "Este estado se calcula automaticamente desde matriculas/pagos."
                : "Disponible para recepcion/secretaria: preinscrito, inscrito o rechazado."}
            </small>
          </div>
          <div className="col-md-6">
            <label className="form-label management-label">
              Centro (opcional)
              <span
                className="ms-1 text-muted"
                title="Selecciona el centro si se matriculará aqui."
                aria-label="Informacion de centro"
              >
                <i className="bi bi-info-circle" aria-hidden="true" />
              </span>
            </label>
            <select
              className="form-select"
              value={form.centro_id}
              onChange={(e) => setForm((prev) => ({ ...prev, centro_id: e.target.value }))}
              disabled={loadingCentros}
            >
              <option value="">Selecciona un centro</option>
              {centros.map((c) => (
                <option key={`centro-${c.id}`} value={String(c.id)}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6 d-flex align-items-center pt-4">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`activo-${isEdit ? "edit" : "new"}`}
                checked={form.activo}
                onChange={(e) => setForm((prev) => ({ ...prev, activo: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor={`activo-${isEdit ? "edit" : "new"}`}>
                Activo
              </label>
            </div>
          </div>
        </div>
        </div>
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal}>
            Cancelar
          </button>
          <button type="submit" className="btn management-btn-primary" disabled={saving}>
            {saving ? "Guardando..." : isEdit ? "Actualizar" : "Crear"}
          </button>
        </div>
      </form>
    );
  };

  return (
    <>
      <Modal open={modal === "crear"} title="Nuevo estudiante" onClose={cerrarModal}>
        {renderForm(formNuevo, setFormNuevo, handleCrear)}
      </Modal>

      <Modal open={modal === "editar"} title="Editar estudiante" onClose={cerrarModal}>
        {renderForm(formEditar, setFormEditar, handleActualizar, true)}
      </Modal>

      <Modal open={modal === "eliminar"} title="Eliminar estudiante" onClose={cerrarModal} size="modal-md">
        <div className="modal-body pt-0">
          Seguro que deseas eliminar al estudiante <strong>{estudianteEditando?.nombres}</strong>?
        </div>
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-outline-secondary" onClick={cerrarModal}>
            Cancelar
          </button>
          <button type="button" className="btn btn-danger" onClick={handleEliminar} disabled={deleting}>
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </Modal>
    </>
  );
}
